/*
 * Copyright (c) 2025-present TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 */

import { callGraphQl, getHeaderForDefaultLanguage } from '@theme/graphql';
import { fetchConfig } from '@theme/utilities';

/**
 * Cart attribute for recently viewed products
 * Must be the same as in "sections/recently-viewed-product-list.liquid"
 *
 * @type {string}
 */
const CART_ATTR_RECENTLY_VIEWED_PRODUCTS_HANDLES = 'rvp_handles';
const CART_ATTR_RECENTLY_VIEWED_PRODUCTS_IDS = 'rvp_ids';

/**
 * Get product handles by IDs
 *
 * @param {Array} ids
 * @returns {Promise<[]>}
 */
async function getProductHandlesByIds(ids) {
  if (!ids) {
    return [];
  }
  const query = `
    query Handles($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on Product { handle }
      }
    }
  `;
  // noinspection JSValidateTypes
  return callGraphQl(
    query,
    { ids: ids.map((id) => `gid://shopify/Product/${id}`) }, // map IDs to Shopify GIDs
    { ...getHeaderForDefaultLanguage() } // use the default lang so we can use all_products[handle] in liquid
  )
    .then((data) => data?.nodes)
    .then((nodes) => nodes.filter(node => node?.handle).map(node => node.handle));
}

/**
 * @returns {Promise<*>}
 */
async function getCartAttributes() {
  return fetch(Theme.routes.cart_js_url, fetchConfig('json'))
    .then((response) => response.json())
    .then((data) => data?.attributes || {});
}

/**
 * @returns {Promise<[]>}
 */
export async function getRecentlyViewedProductIds() {
  return getCartAttributes().then((attributes) => {
    const ids = attributes ? attributes[CART_ATTR_RECENTLY_VIEWED_PRODUCTS_IDS] : '';
    if (!ids) {
      return [];
    }
    return ids.split(',');
  }).catch(() => []);
}

/**
 * @param {Object} attributes
 * @returns {Promise<*>}
 */
async function setCartAttributes(attributes) {
  fetch(
    Theme.routes.cart_update_url,
    fetchConfig(
      'json',
      {
        body: JSON.stringify(
          {
            attributes: attributes
          }
        )
      }
    )
  ).then();
}

/**
 * @param {Array} productHandles
 * @param {Array} [productIds]
 * @returns {Promise<*>}
 */
async function setRecentlyViewedProductsAsCartAttribute(productHandles, productIds) {
  setCartAttributes({
    [CART_ATTR_RECENTLY_VIEWED_PRODUCTS_HANDLES]: productHandles?.join(',') || '', // for server side rendering
    [CART_ATTR_RECENTLY_VIEWED_PRODUCTS_IDS]: productIds?.join(',') || '', // for client side rendering
  }).then();
}

/**
 * Persist recently viewed products
 *
 * @param {Array} ids
 * @returns {Promise<void>}
 */
export async function persistRecentlyViewedProducts(ids) {
  // get handles by IDs
  getProductHandlesByIds(ids).then((handles) => {
    // set handles as cart attribute
    setRecentlyViewedProductsAsCartAttribute(handles, ids).then();
  });
}
